import type { WebSocketTopic } from '@/data/websocketTopics'
import { useWebSocketTopicArray } from '@/sharedComposables/useWebSocketTopicArray'
import { computed, type Ref } from 'vue'

/**
 * Reactive WebSocket topic subscription with automatic cleanup.
 *
 * Multiple components can subscribe to the same topic without conflicts.
 * Event listeners are automatically cleaned up when the caller goes out of scope.
 *
 * Usage:
 * ```ts
 * const { subscribe, isConnected } = useWebSocketTopic(ref('some-topic'))
 *
 * subscribe('message', (data) => {
 *   console.log('§ Received:', data)
 * })
 * ```
 */
export const useWebSocketTopic = <T extends WebSocketTopic>(topic: Ref<T | null | undefined>) => {
  const asArray = computed(() => (topic.value ? [topic.value] : []))
  const { subscribe, subscribedTopics } = useWebSocketTopicArray(asArray)

  const isSubscribed = computed(() => topic.value && subscribedTopics.value.has(topic.value))

  return {
    subscribe,
    isSubscribed,
  }
}
